import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { UnitInvitation } from '@/types';
import { formatDate } from '@/lib/utils';
import { CheckCircle, XCircle, Mail, Clock } from 'lucide-react';

// Invitations aren't client-readable (the token is the credential), so they're loaded and
// declined through callables keyed by the emailed token.
interface InvitationSummary {
  id: string;
  unitId: string;
  unitName: string;
  email: string;
  role: UnitInvitation['role'];
  invitedByName: string;
  status: UnitInvitation['status'];
  createdAt: number;
  expiresAt: number;
}

function callableErrorMessage(error: unknown, fallback: string): string {
  const code = (error as { code?: string })?.code ?? '';
  const message = (error as { message?: string })?.message;
  return code.startsWith('functions/') && code !== 'functions/internal' && message ? message : fallback;
}

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshUserProfile } = useAuth();

  const [invitation, setInvitation] = useState<UnitInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const getUnitInvitation = httpsCallable<{ token: string }, InvitationSummary>(
          functions,
          'getUnitInvitation'
        );
        const { data } = await getUnitInvitation({ token });

        const invite: UnitInvitation = {
          id: data.id,
          unitId: data.unitId,
          unitName: data.unitName,
          email: data.email,
          role: data.role,
          invitedBy: '',
          invitedByName: data.invitedByName,
          status: data.status,
          createdAt: new Date(data.createdAt),
          expiresAt: new Date(data.expiresAt),
          token,
        };

        // Check if invitation is expired
        if (invite.expiresAt < new Date()) {
          invite.status = 'expired';
        }

        // Check if already accepted or declined
        if (invite.status !== 'pending' && invite.status !== 'expired') {
          setError(`This invitation has already been ${invite.status}`);
        }

        setInvitation(invite);
      } catch (error) {
        console.error('Error fetching invitation:', error);
        setError(callableErrorMessage(error, 'Failed to load invitation'));
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleAccept = async () => {
    if (!invitation || !currentUser || !userProfile) {
      showError('You must be logged in to accept this invitation');
      return;
    }

    // Verify email matches
    if (invitation.email.toLowerCase() !== currentUser.email?.toLowerCase()) {
      showError(
        'This invitation was sent to a different email address. Please log in with the correct account.'
      );
      return;
    }

    // Check if already a member
    const memberships = userProfile.units || userProfile.units;
    const isMember = memberships?.some((l: any) => ('unitId' in l ? l.unitId : l.unitId) === invitation.unitId);
    if (isMember) {
      showError('You are already a member of this unit');
      return;
    }

    setProcessing(true);
    try {
      // Unit membership is written server-side (rules don't let members add themselves).
      // Refresh the ID token first so a just-verified email is reflected in its claims.
      await currentUser.getIdToken(true);
      const acceptUnitInvitation = httpsCallable<{ invitationId: string }, { unitId: string }>(
        functions,
        'acceptUnitInvitation'
      );
      await acceptUnitInvitation({ invitationId: invitation.id });

      await refreshUserProfile();
      showSuccess(`You've joined ${invitation.unitName}!`);

      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      showError(callableErrorMessage(error, 'Failed to accept invitation. Please try again.'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation || !token) return;

    setProcessing(true);
    try {
      const declineUnitInvitation = httpsCallable<{ token: string }, { status: string }>(
        functions,
        'declineUnitInvitation'
      );
      await declineUnitInvitation({ token });

      setInvitation({ ...invitation, status: 'declined' });
      showSuccess('Invitation declined');

      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error declining invitation:', error);
      showError(callableErrorMessage(error, 'Failed to decline invitation'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <XCircle className="h-16 w-16 text-destructive" />
              </div>
              <CardTitle className="text-center">Invalid Invitation</CardTitle>
              <CardDescription className="text-center">
                {error || 'This invitation link is not valid'}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (invitation.status === 'expired') {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <Clock className="h-16 w-16 text-muted-foreground" />
              </div>
              <CardTitle className="text-center">Invitation Expired</CardTitle>
              <CardDescription className="text-center">
                This invitation expired on {formatDate(invitation.expiresAt)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-muted rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Please contact {invitation.invitedByName} to request a new invitation to{' '}
                  {invitation.unitName}.
                </p>
              </div>
              <div className="mt-4 flex justify-center">
                <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (invitation.status === 'accepted') {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <CardTitle className="text-center">Already Accepted</CardTitle>
              <CardDescription className="text-center">
                You've already accepted this invitation
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (invitation.status === 'declined') {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <XCircle className="h-16 w-16 text-muted-foreground" />
              </div>
              <CardTitle className="text-center">Invitation Declined</CardTitle>
              <CardDescription className="text-center">
                You've declined this invitation
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <Mail className="h-16 w-16 text-primary" />
            </div>
            <CardTitle className="text-center">You're Invited!</CardTitle>
            <CardDescription className="text-center">
              You've been invited to join a unit
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted rounded-lg p-6 space-y-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Unit</div>
                <div className="text-xl font-bold">{invitation.unitName}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1">Your Role</div>
                <Badge className="text-base px-3 py-1">{invitation.role}</Badge>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1">Invited By</div>
                <div className="font-medium">{invitation.invitedByName}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1">Invitation Sent</div>
                <div className="text-sm">{formatDate(invitation.createdAt)}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-1">Valid Until</div>
                <div className="text-sm">{formatDate(invitation.expiresAt)}</div>
              </div>
            </div>

            {!currentUser && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  You must be logged in to accept this invitation. Please sign in or create an
                  account with the email address <strong>{invitation.email}</strong>
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      navigate(`/login?redirect=/unit/accept-invitation?token=${token}`)
                    }
                  >
                    Sign In
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      navigate(`/signup?redirect=/unit/accept-invitation?token=${token}`)
                    }
                  >
                    Sign Up
                  </Button>
                </div>
              </div>
            )}

            {currentUser &&
              currentUser.email?.toLowerCase() !== invitation.email.toLowerCase() && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    This invitation was sent to <strong>{invitation.email}</strong> but you're
                    logged in as <strong>{currentUser.email}</strong>. Please log in with the
                    correct account.
                  </p>
                </div>
              )}

            {currentUser &&
              currentUser.email?.toLowerCase() === invitation.email.toLowerCase() && (
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={handleAccept}
                    disabled={processing}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    {processing ? 'Processing...' : 'Accept Invitation'}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleDecline}
                    disabled={processing}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Decline
                  </Button>
                </div>
              )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}


